import React, { useEffect, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import Button from "../../components/button";
import { createApplication, fetchUserCompanyDetails, getApplicationById, updateApplication, uploadImage } from "../../core/axios/endpoints";
import { isEenmanszaak, isGeldigBsn } from "../../core/bsn";
import { prefillApplicationForm } from "../../core/application-form-fields";
import { toastrError, toastrSuccess } from "../../utils/alertify";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NETWORK_ERROR, RoutesFullPath } from "../../utils/constants";
import { useUser } from "../../core/context/user";
import FeatherIcon from "feather-icons-react";
import PageLoader from "../../components/page-loader";
import { AantekeningVoorOnsPopOver, AchterNaamPopOver, DatumOpdrachtBevestigingPopOver, EmailPopOver, HandelsNaamPopOver, HuisNummerPopOver, KopieOndertekendeOffertePopOver, KvkNummerPopOver, MeldCodePopOver, PlaatsPopOver, PostCodePopOver, KenmerkPopOver, StraatNaamPopOver, TelefoonNummerPopOver, TypePopOver, VoorLettersPopOver } from "../../components/custom-popovers";

const IsdeAanvragenPage = (props) => {
  const [validationSchema, setValidationSchema] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { userData } = useUser();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [isFullScreenLoading, setIsFullScreenLoading] = useState(false);
  const [updateAppPricing, setUpdateAppPricing] = useState({});
  const [isError, setError] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isAutoFillLoading, setIsAutoFillLoading] = useState(false);
  const [isAutoFill, setIsAutoFill] = useState(false);
  const [KvkNummer, setKvkNummer] = useState('');
  const [lastFetchedKvkNummer, setLastFetchedKvkNummer] = useState('');
  // Rechtsvorm komt uit de KvK-lookup. Een eenmanszaak heeft geen RSIN, dus
  // hebben wij dan het BSN van de eigenaar nodig.
  const [rechtsvorm, setRechtsvorm] = useState('');
  const [uitgebreideRechtsvorm, setUitgebreideRechtsvorm] = useState('');
  const eenmanszaak = isEenmanszaak(rechtsvorm, uitgebreideRechtsvorm);
  const [meldCodes, setMeldCodes] = useState(['']);
  const [type, setType] = useState('');
  const [annualStatment, setAnnualStatment] = useState({});
  const [isStatmentUploadLoading, setIsStatmentUploadLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const applicationId = state?.applicationId;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (applicationId) {
      setIsUpdate(true);
      getAanraagDetails();
    }
  }, [applicationId]);

  const getAanraagDetails = async () => {
    setIsFullScreenLoading(true);
    await getApplicationById(applicationId).then((data) => {
      setUpdateAppPricing({
        exclusiveBTW: data.exclusiveBTW,
        inclusiveBTW: data.inclusiveBTW,
        totaal: data.totaal,
      });
      // Alleen de formuliervelden; interne velden van de aanvraag mogen niet
      // terug naar de server (Joi weigert onbekende keys met een 400).
      prefillApplicationForm(data, setValue);
      setUploadedFiles(data.quotationCopyFiles.map(file => ({ name: getFileNameFromUrl(file), url: file })));
      setMeldCodes(data?.meldCodes || ['']);
      setType(data?.type || '');
      setAnnualStatment({
        name: getFileNameFromUrl(data?.annualStatement || ''),
        url: data?.annualStatement
      } || {});
      console.log(annualStatment);
    }).catch(err => {
      setError(true);
      if (err?.isNetworkError) {
        setIsNetworkError(true);
      }
    }).finally(() => {
      setIsFullScreenLoading(false);
    });
  };

  const getFileNameFromUrl = (url) => {
    // Use URL constructor to parse the given URL
    const parsedUrl = new URL(url);
    // Get the pathname from the URL
    const pathname = parsedUrl.pathname;
    // Split the pathname by '/' and return the last part
    const segments = pathname.split('/');
    return segments.pop();
  }

  // Het validatieschema wordt eenmalig opgebouwd; via een ref leest de test
  // altijd de actuele rechtsvorm in plaats van die van de eerste render.
  const eenmanszaakRef = useRef(false);
  useEffect(() => {
    eenmanszaakRef.current = eenmanszaak;
  }, [eenmanszaak]);

  const schema = () => {
    return Yup.object().shape({
      KvkNummer: Yup.string().required("KVK Nummer Klant is verplicht"),
      VoorLetters: Yup.string().required("Voor letters is verplicht"),
      geslacht: Yup.string().required("Geslacht is verplicht"),
      postCode: Yup.string()
        .required("Postcode is verplicht")
        .matches(/^[1-9][0-9]{3}\s?[a-zA-Z]{2}$/, "Invalid postcode format"),
      straatNaam: Yup.string().required("Straat Naam is verplicht"),
      email: Yup.string().required("Email is verplicht").email("Email is invalid"),
      Kenmerk: Yup.string().required("Kenmerk is verplicht"),
      aantekeningVoorOns: Yup.string().optional(),
      handelsNaam: Yup.string().required("Handels Naam is verplicht"),
      bsn: Yup.string().test(
        'bsn-verplicht-bij-eenmanszaak',
        'Vul een geldig BSN in (9 cijfers).',
        (value) => !eenmanszaakRef.current || isGeldigBsn(value),
      ),
      achterNaam: Yup.string().required("Achter Naam is verplicht"),
      huisNummer: Yup.string().required("Huis Nummer is verplicht"),
      plaats: Yup.string().required("Plaats is verplicht"),
      teleFoonNummer: Yup.string().required("Telefoon Nummer is verplicht"),
      datumOpdrachtBevestiging: Yup.string()
        .required('Datum Opdracht Bevestiging is verplicht')
        .test('is-date', 'Invalid date. Required format: yyyy-mm-dd', (value) => {
          return /^(\d{4})-(\d{2})-(\d{2})$/.test(value);
        }),
    });
  };

  useEffect(() => {
    setValidationSchema(schema());
  }, []);

  const submitForm = (data) => {


    if (isMeldCodeInvalid()) {
      toastrError(isMeldCodeInvalid());
      return;
    }

    if (isTypeWarmtepomp() && !isAnnualStatmentUploaded()) {
      toastrError('Please upload the annual statement');
      return;
    }

    const getQuotationFiles = () => {
      return uploadedFiles.map((file) => file.url);
    };

    const FilteredMeldCodes = meldCodes?.filter(code => code).map(code => code.trim());

    setIsLoading(true);
    if (isUpdate) {
      updateApplication(applicationId, {
        ...data, regling: 'ISDE', quotationCopyFiles: getQuotationFiles() || [],
        meldCodes: FilteredMeldCodes, annualStatement: annualStatment?.url, type: type
      })
        .then((res) => {
          toastrSuccess(res?.successMsg || "Aanvraag succesvol geüpload.");
          navigate(RoutesFullPath.customerDashboard);
        })
        .catch((err) => {

          toastrError(err?.response?.data?.error || "Er is een fout opgetreden. Probeer het later opnieuw.");
        })
        .finally(() => {
          setIsLoading(false);
          setIsSubmitted(false);
        });
    } else {
      createApplication({
        ...data, regling: 'ISDE', quotationCopyFiles: getQuotationFiles() || [],
        meldCodes: FilteredMeldCodes, annualStatement: annualStatment?.url, type: type
      })
        .then((res) => {
          toastrSuccess(res?.successMsg || "Aanvraag succesvol aangemaakt.");
          navigate(RoutesFullPath.customerDashboard);
        })
        .catch((err) => {

          toastrError(err?.response?.data?.error || "Er is een fout opgetreden. Probeer het later opnieuw.");
        })
        .finally(() => {
          setIsLoading(false);
          setIsSubmitted(false);
        });
    }
  };

  const uploadFile = (e, isStatement) => {
    const file = e.target.files[0];

    if (isStatement) {
      setIsStatmentUploadLoading(true);
    } else {
      setIsUploadLoading(true);
    }

    uploadImage(file).then((res) => {
      if (isStatement) {
        setAnnualStatment({
          name: file.name,
          url: res?.data?.url,
        });
        return;
      }
      setUploadedFiles((prev) => [...prev, { name: file.name, url: res?.data?.url }]);
    }).catch((err) => {

    }).finally(() => {
      setIsUploadLoading(false);
      setIsStatmentUploadLoading(false);
    });
  };

  const selectAndUploadFile = (isStatement) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf, .jpg, .jpeg, .png';
    input.onchange = (e) => {
      uploadFile(e, isStatement);
    };
    input.click();
  };

  const openUrlInNewTab = (url) => {
    window.open(url, '_blank');
  }

  const isKvkNummerValid = () => {
    if (!KvkNummer || KvkNummer.length < 8 || KvkNummer.length > 8 || isNaN(KvkNummer)) {
      return false;
    }
    return true;
  }
  const getKvkData = async () => {
    if (!isKvkNummerValid()) {
      toastrError('Vul een geldig KVK-nummer in.');
      return;
    }
    setIsAutoFillLoading(true);
    await fetchUserCompanyDetails(KvkNummer).then(data => {

      Object.keys(data).forEach((key) => {
        setValue(key, data[key]);
      });
      setRechtsvorm(data?.rechtsvorm ?? '');
      setUitgebreideRechtsvorm(data?.uitgebreideRechtsvorm ?? '');
      setIsAutoFill(true);
      setLastFetchedKvkNummer(KvkNummer);
    }).catch(err => {
      toastrError(err?.response?.data?.error || "Er is een fout opgetreden. Probeer het later opnieuw.");

    }).finally(() => {
      setIsAutoFillLoading(false);
    });
  }

  const checkNewKvkNummer = (kvk) => {
    if (kvk !== lastFetchedKvkNummer || !kvk) {
      setIsAutoFill(false);
      setRechtsvorm('');
      setUitgebreideRechtsvorm('');
    } else {
      setIsAutoFill(true);
    }
  }

  const formatNumber = (number) => {
    return number.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };


  const createMeldCode = () => {
    if (meldCodes.length < 5) {
      setMeldCodes((prev) => [...prev, '']);
    }
  }

  const removeMeldCode = (index) => {
    setMeldCodes((prev) => prev.filter((item, i) => i !== index));
  }

  const isMeldCodeInvalid = () => {
    const isFirstEmpty = meldCodes[0] === '';
    if (isFirstEmpty) {
      return 'Voeg minimaal één meldCodes toe.';
    }
    return '';
  }

  const isTypeWarmtepomp = () => {
    return type === 'warmtepomp';
  }

  const isTypeZonneboiler = () => {
    return type === 'zonneboiler';
  }

  const isAnnualStatmentUploaded = () => {
    return !!annualStatment?.url;
  }


  if (isFullScreenLoading) {
    return <PageLoader />;
  }

  if (isError) {
    return (
      <div className="main-wrapper">
        <div className="error-box">
          <h1>{isNetworkError ? "503" : "404"}</h1>
          <h3 className="h2 mb-3">
            <i className="fas fa-exclamation-circle" /> Oops! {isNetworkError ? "Network Error!" : ""}
          </h3>
          <p className="h4 font-weight-normal mt-2">
            {isNetworkError ? NETWORK_ERROR : "Er is iets misgegaan. Probeer het later opnieuw."}
          </p>
          <button onClick={() => navigate(-1)} className="customized-btn mt-6">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content client-eia-aanvragen-page">
          <header className="mb-6">
            <nav className="mb-1 text-xs text-ink-subtlest">
              <Link to={RoutesFullPath.customerDashboard} className="hover:underline">Aanvragen</Link>
              <span className="mx-1">/</span>
              <span>{isUpdate ? "Aanvraag bewerken" : "Nieuwe aanvraag"}</span>
            </nav>
            <h1 className="text-2xl font-medium text-ink">ISDE-aanvraag</h1>
            <p className="mt-1 text-sm text-ink-subtlest">
              Vul de gegevens van de onderneming en de installatie in en voeg de offerte toe.
            </p>
          </header>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <form
              className="update-profile-form space-y-4"
              onSubmit={handleSubmit((data) => { submitForm(data); })}
            >

              {/* Onderneming */}
              <section className="rounded bg-white p-5 shadow-card">
                <h2 className="mb-3 text-base font-semibold text-ink">Onderneming</h2>
                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">

                <div className="relative mb-4">
                  <label htmlFor="KvkNummer"> KvK-Nummer Klant </label>
                  <KvkNummerPopOver />
                  <input
                    id="KvkNummer"
                    name="KvkNummer"
                    type="text"
                    autoComplete="new-password"
                    onInput={(e) => { setKvkNummer(e.target.value); checkNewKvkNummer(e.target.value) }}
                    className={`${errors.KvkNummer ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full !pr-44`}
                    {...register("KvkNummer")}
                  />
                  {<button disabled={isAutoFill || !isKvkNummerValid() || isAutoFillLoading} type="button" className="absolute top-[28px] right-[36px] kvk-nummer-btn" onClick={getKvkData}>
                    {isAutoFillLoading ? <span className="loading loading-spinner loading-xs"></span> : ''}
                    {isAutoFillLoading ? 'Fetching..' : isAutoFill ?
                      <FeatherIcon icon="check-circle" className="h-4 w-4 text-[#1F845A]" />
                      : 'Automatisch invullen'}
                  </button>}
                  <div className="invalid-feedback min-h-4">
                    {errors.KvkNummer?.message}
                  </div>
                </div>

                <div className="relative mb-4">
                  <label > Handels Naam </label>
                  <HandelsNaamPopOver />
                  <input
                    id="handelsNaam"
                    name="handelsNaam"
                    type="text"
                    autoComplete="new-password"
                    className={`${errors.handelsNaam ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full`}
                    {...register("handelsNaam")}
                  />
                  <div className="invalid-feedback min-h-4">
                    {errors.handelsNaam?.message}
                  </div>
                </div>

                {/* Een eenmanszaak heeft geen RSIN bij de KvK. Zonder BSN van
                    de eigenaar kunnen wij de aanvraag niet indienen, dus vragen
                    we het meteen uit zodra de lookup een eenmanszaak teruggeeft. */}
                {eenmanszaak && (
                  <div className="relative mb-4">
                    <label htmlFor="bsn">BSN eigenaar</label>
                    <input
                      id="bsn"
                      name="bsn"
                      type="text"
                      inputMode="numeric"
                      maxLength={9}
                      autoComplete="off"
                      placeholder="123456782"
                      className={`${errors.bsn ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full`}
                      {...register("bsn")}
                    />
                    <p className="mt-1 text-xs text-ink-subtlest">
                      Uit de KvK blijkt een eenmanszaak. Die heeft geen RSIN, dus
                      hebben wij het BSN van de eigenaar nodig voor de aanvraag.
                    </p>
                    <div className="invalid-feedback min-h-4">
                      {errors.bsn?.message}
                    </div>
                  </div>
                )}

                <div className="relative mb-4">
                  <label htmlFor="Kenmerk"> Uw Kenmerk </label>
                  <KenmerkPopOver />
                  <input
                    id="Kenmerk"
                    name="Kenmerk"
                    type="text"
                    autoComplete="new-password"
                    className={`${errors.Kenmerk ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full`}
                    {...register("Kenmerk")}
                  />
                  <div className="invalid-feedback min-h-4">
                    {errors.Kenmerk?.message}
                  </div>
                </div>

                </div>
              </section>

              {/* Contactpersoon */}
              <section className="rounded bg-white p-5 shadow-card">
                <h2 className="mb-3 text-base font-semibold text-ink">Contactpersoon</h2>
                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">

                <div className="relative mb-4">
                  <label htmlFor="VoorLetters">Voorletters </label>
                  <VoorLettersPopOver />
                  <input
                    id="VoorLetters"
                    name="VoorLetters"
                    type="text"
                    autoComplete="new-password"
                    className={`${errors.VoorLetters ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full`}
                    {...register("VoorLetters")}
                  />
                  <div className="invalid-feedback min-h-4">
                    {errors.VoorLetters?.message}
                  </div>
                </div>

                <div className="relative mb-4">
                  <label htmlFor="achterNaam"> Achternaam </label>
                  <AchterNaamPopOver />
                  <input
                    id="achterNaam"
                    name="achterNaam"
                    type="text"
                    autoComplete="new-password"
                    className={`${errors.achterNaam ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full`}
                    {...register("achterNaam")}
                  />
                  <div className="invalid-feedback min-h-4">
                    {errors.achterNaam?.message}
                  </div>
                </div>

                <div className="relative mb-4">
                  <label htmlFor="geslacht"> Geslacht</label>
                  <div className="flex gap-6 py-1.5">
                    <div className="form-control">
                      <label className="mb-0 inline-flex cursor-pointer items-center gap-2 text-sm font-normal text-ink">
                        <input type="radio" id="man" name="man" value={'man'} className="h-4 w-4 accent-brand" {...register("geslacht")} />
                        <span className="label-text">Man</span>
                      </label>
                    </div>
                    <div className="form-control">
                      <label className="mb-0 inline-flex cursor-pointer items-center gap-2 text-sm font-normal text-ink">
                        <input type="radio" id="vrouw" name="vrouw" value={'vrouw'} className="h-4 w-4 accent-brand" {...register("geslacht")} />
                        <span className="label-text">Vrouw</span>
                      </label>
                    </div>
                  </div>

                  <div className="invalid-feedback min-h-4">
                    {errors.geslacht?.message}
                  </div>
                </div>

                <div className="relative mb-4">
                  <label htmlFor="teleFoonNummer">Telefoonnummer</label>
                  <TelefoonNummerPopOver />
                  <input
                    id="teleFoonNummer"
                    name="teleFoonNummer"
                    type="text"
                    autoComplete="new-password"
                    className={`${errors.teleFoonNummer ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full`}
                    {...register("teleFoonNummer")}
                  />
                  <div className="invalid-feedback min-h-4">
                    {errors.teleFoonNummer?.message}
                  </div>
                </div>

                <div className="relative mb-4">
                  <label htmlFor="email"> Email</label>
                  <EmailPopOver />
                  <input
                    id="email"
                    name="email"
                    type="text"
                    autoComplete="new-password"
                    className={`${errors.email ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full`}
                    {...register("email")}
                  />
                  <div className="invalid-feedback min-h-4">
                    {errors.email?.message}
                  </div>
                </div>

                </div>
              </section>

              {/* Adres */}
              <section className="rounded bg-white p-5 shadow-card">
                <h2 className="mb-3 text-base font-semibold text-ink">Adres</h2>
                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">

                <div className="relative mb-4">
                  <label htmlFor="straatNaam">Straatnaam </label>
                  <StraatNaamPopOver />
                  <input
                    id="straatNaam"
                    name="straatNaam"
                    type="text"
                    autoComplete="new-password"
                    className={`${errors.straatNaam ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full`}
                    {...register("straatNaam")}
                  />
                  <div className="invalid-feedback min-h-4">
                    {errors.straatNaam?.message}
                  </div>
                </div>

                <div className="relative mb-4">
                  <label htmlFor="huisNummer"> Huisnummer </label>
                  <HuisNummerPopOver />
                  <input
                    id="huisNummer"
                    name="huisNummer"
                    type="text"
                    autoComplete="new-password"
                    className={`${errors.huisNummer ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full`}
                    {...register("huisNummer")}
                  />
                  <div className="invalid-feedback min-h-4">
                    {errors.huisNummer?.message}
                  </div>
                </div>

                <div className="relative mb-4">
                  <label htmlFor="postCode">Postcode</label>
                  <PostCodePopOver />
                  <input
                    id="postCode"
                    name="postCode"
                    type="text"
                    autoComplete="new-password"
                    className={`${errors.postCode ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full`}
                    {...register("postCode")}
                  />
                  <div className="invalid-feedback min-h-4">
                    {errors.postCode?.message}
                  </div>
                </div>

                <div className="relative mb-4">
                  <label htmlFor="plaats">Plaats </label>
                  <PlaatsPopOver />
                  <input
                    id="plaats"
                    name="plaats"
                    type="text"
                    autoComplete="new-password"
                    className={`${errors.plaats ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full`}
                    {...register("plaats")}
                  />
                  <div className="invalid-feedback min-h-4">
                    {errors.plaats?.message}
                  </div>
                </div>

                </div>
              </section>

              {/* Subsidie / installatie */}
              <section className="rounded bg-white p-5 shadow-card">
                <h2 className="mb-3 text-base font-semibold text-ink">Subsidie</h2>
                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">

                <div className="relative mb-4">
                  <label htmlFor="datumOpdrachtBevestiging">Datum Opdracht Bevestiging </label>
                  <DatumOpdrachtBevestigingPopOver position="top-[35px] right-[5px]" />
                  <input
                    id="datumOpdrachtBevestiging"
                    name="datumOpdrachtBevestiging"
                    type="date"
                    autoComplete="new-password"
                    className={`${errors.datumOpdrachtBevestiging ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full`}
                    {...register("datumOpdrachtBevestiging")}
                  />
                  <div className="invalid-feedback min-h-4">
                    {errors.datumOpdrachtBevestiging?.message}
                  </div>
                </div>

                <div className="relative mb-4">
                  <label htmlFor="type">Type</label>
                  <TypePopOver position="top-[2px] right-[5px]" />

                  <div className="flex gap-6 py-1.5">
                    <div className="form-control">
                      <label className="mb-0 inline-flex cursor-pointer items-center gap-2 text-sm font-normal text-ink">
                        <input type="radio" id="warmtepomp" name="type" value="warmtepomp" className="h-4 w-4 accent-brand"
                          checked={isTypeWarmtepomp()} onChange={() => setType('warmtepomp')}
                        />
                        <span className="label-text">Warmtepomp</span>
                      </label>
                    </div>
                    <div className="form-control">
                      <label className="mb-0 inline-flex cursor-pointer items-center gap-2 text-sm font-normal text-ink">
                        <input type="radio" id="zonneboiler" name="type" value="zonneboiler" className="h-4 w-4 accent-brand"
                          checked={isTypeZonneboiler()} onChange={() => setType('zonneboiler')}
                        />
                        <span className="label-text">Zonneboiler</span>
                      </label>
                    </div>
                  </div>

                  {isTypeWarmtepomp() && !isAnnualStatmentUploaded() &&
                    <button disabled={isStatmentUploadLoading || isAnnualStatmentUploaded()} type="button" onClick={() => selectAndUploadFile(true)} className="mt-2 inline-flex h-8 w-full items-center justify-center gap-2 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50">
                      {isStatmentUploadLoading ? <span className="loading loading-spinner loading-xs"></span> : <FeatherIcon icon="upload" size={16} className="text-ink-subtlest" />}
                      {isStatmentUploadLoading ? 'Uploaden, even geduld a.u.b...' : !isAnnualStatmentUploaded() ? 'Upload Energienota' : ''}
                    </button>
                  }

                  {
                    isTypeWarmtepomp() && isAnnualStatmentUploaded() &&
                    <div className="mt-2 flex items-center justify-between gap-2 rounded bg-sunken px-3 py-1.5 text-sm text-ink">
                      <div className="min-w-0 cursor-pointer truncate text-brand hover:underline" onClick={() => openUrlInNewTab(annualStatment?.url)}>{annualStatment?.name?.slice(0, 30)}</div>
                      <div className="cursor-pointer text-ink-subtlest hover:text-[#AE2A19]" onClick={() => setAnnualStatment({})}>
                        <FeatherIcon icon="x-circle" className="h-5" />
                      </div>
                    </div>
                  }

                  <div className="invalid-feedback min-h-4">
                    {
                      isSubmitted && !type ? 'Kies een type' : '' ||
                        isSubmitted && isTypeWarmtepomp() && !isAnnualStatmentUploaded() ?
                        'Upload uw  Energienota alsjeblieft' : ''
                    }
                  </div>
                </div>

                <div className="relative mb-4">
                  <label htmlFor="meldCode"> Meldcodes </label>
                  <MeldCodePopOver position="top-[4px] right-[5px]" />

                  {meldCodes.length < 5 &&
                    <a onClick={createMeldCode}
                      className={`max-w-[400px] absolute top-[4px] right-[35px] cursor-pointer text-ink-subtlest hover:text-brand`} >
                      <FeatherIcon icon="plus-circle" className="info-icon" />
                    </a>
                  }
                  <div className="space-y-2">
                  {
                    meldCodes.map((code, index) => (
                      <div key={index} className="flex gap-2 items-center relative">
                        <input
                          id="meldCode"
                          name="meldCode"
                          type="text"
                          onInput={(e) => {
                            const value = e.target.value;
                            setMeldCodes((prev) => prev.map((item, i) => i === index ? value : item));
                          }}
                          value={code}
                          autoComplete="new-password"
                          className={`appearance-none w-full`}
                        />
                        {index !== 0 &&
                          <FeatherIcon icon="x-circle" className="h-[19px] text-ink-subtlest hover:text-[#AE2A19] cursor-pointer absolute right-[8px] top-[9px]" onClick={() => removeMeldCode(index)} />
                        }
                      </div>
                    ))
                  }
                  </div>

                  {isMeldCodeInvalid() && <div className="invalid-feedback min-h-4">
                    {
                      isSubmitted ? isMeldCodeInvalid() : ''
                    }
                  </div>
                  }
                </div>

                </div>
              </section>

              {/* Documenten en opmerkingen */}
              <section className="rounded bg-white p-5 shadow-card">
                <h2 className="mb-3 text-base font-semibold text-ink">Documenten</h2>
                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">

                <div className="relative mb-4">
                  <label htmlFor="teleFoonNummer">kopie offerte</label>
                  <KopieOndertekendeOffertePopOver position="top-[2px] right-[5px]" />
                  <div className="uploaded-files space-y-1">
                    {uploadedFiles?.length > 0 && uploadedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between gap-2 rounded bg-sunken px-3 py-1.5 text-sm text-ink">
                        <div className="min-w-0 cursor-pointer truncate text-brand hover:underline" onClick={() => openUrlInNewTab(file?.url)}>{file?.name?.slice(0, 30)}</div>
                        <div className="cursor-pointer text-ink-subtlest hover:text-[#AE2A19]" onClick={() => { setUploadedFiles((prev) => prev.filter((item, i) => i !== index)) }}>
                          <FeatherIcon icon="x-circle" className="h-5" />
                        </div>
                      </div>
                    ))}
                  </div>
                  {uploadedFiles?.length < 5 && (
                    <button disabled={isUploadLoading} type="button" onClick={() => selectAndUploadFile(false)} className="mt-2 inline-flex h-8 w-full items-center justify-center gap-2 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50">
                      {isUploadLoading ? <span className="loading loading-spinner loading-xs"></span> : <FeatherIcon icon="upload" size={16} className="text-ink-subtlest" />}
                      {isUploadLoading ? 'Uploaden, even geduld a.u.b...' : 'Upload uw offerte'}
                    </button>
                  )}
                </div>

                <div className="relative mb-4">
                  <label htmlFor="aantekeningVoorOns"> Aantekening Voor Ons </label>
                  <AantekeningVoorOnsPopOver position="top-[4px] right-[5px]" />
                  <textarea
                    id="aantekeningVoorOns"
                    name="aantekeningVoorOns"
                    type="text"
                    rows={2}
                    autoComplete="new-password"
                    className={`${errors.aantekeningVoorOns ? "is-invalid" : isSubmitted && "valid"} appearance-none w-full`}
                    {...register("aantekeningVoorOns")}
                  />
                  <div className="invalid-feedback min-h-4">
                    {errors.aantekeningVoorOns?.message}
                  </div>
                </div>

                </div>
              </section>

              <div className="flex justify-end pt-2">
                {isUpdate ?
                  <Button btnClassNames={`inline-flex h-8 items-center justify-center rounded bg-brand px-4 text-sm font-medium text-white hover:bg-brand-hover`} text="Indienen" isLoading={isLoading} onClick={() => { handleSubmit(); setIsSubmitted(true); }} loadingText="indienen..." />
                  :
                  <Button btnClassNames={`inline-flex h-8 items-center justify-center rounded bg-brand px-4 text-sm font-medium text-white hover:bg-brand-hover`} text="Verstuur" isLoading={isLoading} onClick={() => { handleSubmit(); setIsSubmitted(true); }} loadingText="Verzending..." />
                }
              </div>
            </form>

            <aside className="lg:sticky lg:top-20 lg:self-start">
              <div className="details-card rounded bg-white p-5 shadow-card">
                <h2 className="text-base font-semibold text-ink">Factuurinformatie</h2>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="font-normal text-ink-subtlest">Factuur aan:</dt>
                    <dd className="text-right"><strong>{userData?.naam}</strong></dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="font-normal text-ink-subtlest">Bedrag exclusief BTW:</dt>
                    <dd className="tabular-nums"><strong>€{
                      isUpdate ? formatNumber(updateAppPricing?.exclusiveBTW) : formatNumber(userData?.applicationFee)
                    }</strong></dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-4 border-t border-line pt-2">
                    <dt className="font-normal text-ink-subtlest">Bedrag inclusief BTW:</dt>
                    <dd className="tabular-nums"><strong>€{
                      isUpdate ? formatNumber(updateAppPricing?.exclusiveBTW + updateAppPricing?.inclusiveBTW) : formatNumber(userData?.applicationFee + userData?.tax)
                    }</strong></dd>
                  </div>
                </dl>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default IsdeAanvragenPage;
